import {
  formatDateOnlyShort,
  parseJson,
} from '@/modules/service-requests/shared/application-utils';
import { NocApplication, NocCopyType, NocGeneratedEntry } from '../types/noc.types';

/** Existing UILM labels; the third option is shown as "Both". */
export const NOC_COPY_TYPES: { value: NocCopyType; labelKey: string }[] = [
  { value: 'HardCopy', labelKey: 'HARD_COPY' },
  { value: 'SoftCopy', labelKey: 'SOFT_COPY' },
  { value: 'Booth', labelKey: 'BOTH' },
];

export interface NocTypeRequirements {
  passportRequired: boolean;
  datesRequired: boolean;
  destinationRequired: boolean;
  /** A passport NOC is not for travel, so the destination is cleared and hidden. */
  isPassportType: boolean;
}

/**
 * The legacy form's type-dependent validation, driven by the NOC type's *name*: a visa NOC needs a
 * passport number; a visa NOC that is not "without ..." also needs travel dates and a destination.
 */
export const nocTypeRequirements = (typeName: string | undefined): NocTypeRequirements => {
  const name = (typeName ?? '').toLowerCase();
  const passportRequired = name.includes('visa');
  const datesRequired = name.includes('visa') && !name.includes('without');
  const isPassportType = name.includes('passport');
  return {
    passportRequired,
    datesRequired,
    isPassportType,
    destinationRequired: !isPassportType && datesRequired,
  };
};

/** Legacy UI: an applicant may edit while pending, or after approval (to fix the letter details). */
export const canEditNoc = (application: NocApplication) =>
  application.Status === 'Pending' || application.Status === 'Accepted';

export const canReviewNoc = (application: NocApplication) => application.Status === 'Pending';

export const canGenerateNoc = (application: NocApplication) =>
  application.Status === 'Accepted' || application.Status === 'Generated';

/** Legacy `DeleteNocApplicationAsync`: an accepted or generated NOC can never be deleted, even by an admin. */
export const canDeleteNocAsAdmin = (application: NocApplication) =>
  application.Status === 'Pending' || application.Status === 'Rejected';

/**
 * A date-only field (travel dates, joining date) as `21-Sep-2026` by its date part, or `N/A` when
 * missing (letters never show a blank).
 */
export const formatLetterDateOnly = (value?: string | null) => formatDateOnlyShort(value, 'N/A');

export const pronounsFor = (sex: string | undefined) => {
  const female = (sex ?? '').toLowerCase() === 'female';
  return { subject: female ? 'She' : 'He', object: female ? 'her' : 'him' };
};

interface LetterContext {
  application: Pick<
    NocApplication,
    | 'Name'
    | 'Designation'
    | 'PassportNumber'
    | 'TravelStartDate'
    | 'TravelEndDate'
    | 'TravelDestination'
  >;
  joiningDate?: string;
  sex?: string;
}

/** Fills the NOC type's letter template with the applicant's details. */
export const renderNocLetterBody = (
  template: string | undefined,
  { application, joiningDate, sex }: LetterContext
) => {
  if (!template) return '';
  const { subject, object } = pronounsFor(sex);
  return (
    template
      .replace(/\{Name\}/g, application.Name || '')
      .replace(/\{Designation\}/g, application.Designation || 'N/A')
      .replace(/\{PassportNumber\}/g, application.PassportNumber || 'N/A')
      // Date-only fields: read by their date part, never converted to a zone.
      .replace(/\{JoiningDate\}/g, formatLetterDateOnly(joiningDate))
      .replace(/\{TravelStartDate\}/g, formatLetterDateOnly(application.TravelStartDate))
      .replace(/\{TravelEndDate\}/g, formatLetterDateOnly(application.TravelEndDate))
      .replace(/\{TravelPlace\}/g, application.TravelDestination || 'N/A')
      .replace(/\{Subject\}/g, subject)
      .replace(/\{Object\}/g, object)
      .replace(/\{SubjectLower\}/g, subject.toLowerCase())
  );
};

/**
 * Splits `SELISEBD/NOC/2026/09/03 21-Sep-2026` into the reference number and the letter date. A
 * number without a trailing date takes `fallbackDate`.
 */
export const parseDocumentNumber = (input: string | undefined, fallbackDate: string) => {
  if (!input) return { number: 'N/A', date: fallbackDate };
  const match = input.match(/(\d{1,2}-[A-Za-z]{3,9}-\d{2,4})$/);
  if (!match) return { number: input, date: fallbackDate };
  return {
    number: input.substring(0, input.length - match[1].length).replace(/[\s/-]+$/, ''),
    date: match[1],
  };
};

/** The most recent time the letter was generated, or null if it never was. */
export const latestGeneratedEntry = (application: Pick<NocApplication, 'GeneratedHistories'>) => {
  const history = parseJson<NocGeneratedEntry[]>(application.GeneratedHistories, []);
  return history.length ? history[history.length - 1] : null;
};

/** Defaults the legacy "information for NOC document" dialog pre-filled for the point of contact. */
export const DEFAULT_POINT_OF_CONTACT = {
  pocName: 'Adnan Raihan',
  pocDesignation: 'Principal Admin Manager',
  pocEmail: 'adnan.raihan@selisegroup.com',
  pocMobile: '+8801711126465',
};
